import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  exhaustMap,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';

import { EffectsBase } from '@ps/core-data-access';

import { retryServerError } from '../../utils/retry-server-error.operator';

import { AuthActions, SettingsActions } from '../actions/auth.actions';
import { selectUser } from '../selectors/auth.selectors';

import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';

const RETRY = {
  count: 3,
  delay: 500,
};

@Injectable()
export class AuthEffects extends EffectsBase {
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _authService = inject(AuthService);
  private readonly _settingsService = inject(SettingsService);

  private readonly _router = inject(Router);

  public login$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap((credentials) =>
        this._authService.login(credentials).pipe(
          retryServerError(RETRY.count, RETRY.delay),
          map((user) => AuthActions.loginSuccess({ user })),
          this.psCatchError(
            (error) => of(AuthActions.loginFailure({ error: error.message })),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loginRedirect$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(() => this._router.navigate(['/dashboard'])),
      ),
    { dispatch: false },
  );

  public logout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.logout),
      concatLatestFrom(() => this._store.select(selectUser)),
      exhaustMap(([, user]) =>
        user.match({
          some: ({ id }) =>
            this._authService.logout(id).pipe(
              map(() => AuthActions.logoutSuccess()),
              catchError((error) => of(AuthActions.logoutFailure({ error }))),
            ),
          none: () => of(AuthActions.logoutSuccess()),
        }),
      ),
    ),
  );

  public logoutRedirect$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => this._router.navigate(['/login'])),
      ),
    { dispatch: false },
  );

  public settings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.loginSuccess),
      switchMap(({ user }) =>
        forkJoin({
          allBuyerSettings: this._settingsService.getAllBuyerSettings(),
          companySettings: this._settingsService.getCompanySettings(
            user.companyId,
          ),
        }).pipe(
          map(({ allBuyerSettings, companySettings }) =>
            SettingsActions.loadSettingsSuccess({
              allBuyerSettings,
              companySettings,
            }),
          ),
          this.psCatchError(() => of(AuthActions.logout), {
            notify: true,
            customMessage:
              'Unable to proceed right now. Please try again later.',
          }),
        ),
      ),
    ),
  );
}
